
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Vessel } from '@/models/Vessel'
import { namespace } from 'vuex-class'
import Notification from '@/classes/Notification'
import { TrackTraceHash } from '@/models/TrackTraceHash'
import { Transport } from '@/models/Transport'
import i18n from '@/i18n'
import { checkIfEmailAddressIsValid } from '@/utils/email'
import moment from 'moment'
import { Cargo } from '@/models/Cargo'
import { Terminal } from '@/models/Terminal'
import { Company } from '@/models/Company'
import DOMPurify from 'dompurify'

const mapModule = namespace('map')

@Component({
	components: {}
})
export default class TrackAndTraceShare extends Vue {
	emailAddresses: Array<string> = []
	comments: string = ''

	loading: boolean = false
	errorMessage: string = ''

	@Prop({ required: true })
	transportId!: string

	@Prop({ required: true })
	cargoId!: string

	@Prop()
	showDialog!: boolean

	@mapModule.State('vessels')
	readonly vessels!: Array<Vessel>

	@mapModule.Action('setVessels')
	setVessels: any

	transport: Transport | null = null
	startTerminal: Terminal | null = null
	endTerminal: Terminal | null = null
	cargo: Cargo | null = null
	client: Company | null = null

	startTerminalEmail: string = ''
	endTerminalEmail: string = ''
	clientEmail: string = ''
	skipperEmail: string = ''
	isLoading: boolean = false

	async created() {
		this.isLoading = true
		await this.getEmails()
		this.isLoading = false
	}

	@Watch('isLoading')
	@Watch('showDialog')
	prefillHandler() {
		if (!this.isLoading && this.showDialog) this.prefillBasedOnPreviousValues()
	}

	get emailAddressesWithLabels() {
		const emailProperties = {
			clientEmail: 'client_email',
			skipperEmail: 'skipper_email',
			startTerminalEmail: 'start_terminal_email',
			endTerminalEmail: 'end_terminal_email'
		}

		const emailAddressesWithLabels = [] as Array<{
			value: string
			text: string
		}>

		for (const property in emailProperties) {
			if (this[property]) {
				emailAddressesWithLabels.push({
					value: this[property],
					text: i18n.t('track-and-trace.' + emailProperties[property]) as string
				})
			}
		}
		return emailAddressesWithLabels
	}

	get localStorageKey(): string {
		return this.endTerminal?.getApiId() + '_' + this.client?.getApiId()
	}

	prefillBasedOnPreviousValues() {
		const localStoragedData = JSON.parse(localStorage.getItem(this.localStorageKey) ?? '{}')

		for (const email in localStoragedData) {
			if (localStoragedData[email] >= 5) {
				this.emailAddresses.push(email)
			}
		}
	}

	async getEmails() {
		const transportQuery = Transport.find(this.transportId)
		const cargoQuery = Cargo.find(this.cargoId)
		this.transport = (await transportQuery).getData() as Transport
		this.cargo = (await cargoQuery).getData() ?? null

		if (this.cargo) {
			this.client = this.cargo.getClient()
			this.clientEmail = this.client?.email ?? ''

			this.startTerminal = this.cargo.getStartTerminal()
			this.startTerminalEmail = this.startTerminal?.primaryEmail ?? ''

			this.endTerminal = this.cargo.getDestinationTerminal()
			this.endTerminalEmail = this.endTerminal?.primaryEmail ?? ''
		}

		const skipper = this.transport.getSkipper() ?? (await this.transport.fresh())!.getSkipper()
		this.skipperEmail = skipper?.email ?? ''

		this.isLoading = false
	}

	removeEmail(item) {
		this.emailAddresses.splice(this.emailAddresses.indexOf(item), 1)
		this.emailAddresses = [...this.emailAddresses]
	}

	dateFormat(date: string, format: string = 'dd DD MMM HH:mm'): string {
		const formattedWithTime = moment(date).format(format)
		if (formattedWithTime.endsWith(' 00:00')) {
			return formattedWithTime.replace(' 00:00', '')
		}
		return formattedWithTime
	}

	onSameDate(firstDate: string, secondDate: string) {
		return moment(firstDate).isSame(moment(secondDate), 'day')
	}

	formatLoadingTerm(loadPeriodFromDate, loadPeriodFromTime, loadPeriodUntilDate) {
		if (!loadPeriodFromTime && !this.onSameDate(loadPeriodFromDate, loadPeriodUntilDate)) {
			return (
				this.dateFormat(loadPeriodFromDate, 'dd DD MMM') +
				' - ' +
				this.dateFormat(loadPeriodUntilDate, 'dd DD MMM')
			)
		} else if (!loadPeriodFromTime) {
			return this.dateFormat(loadPeriodFromDate, 'dd DD MMM')
		} else {
			return this.dateFormat(loadPeriodFromTime, 'dd DD MMM HH:mm')
		}
	}

	get loadingTerm() {
		return this.formatLoadingTerm(
			this.cargo?.loadPeriodFromDate,
			this.cargo?.loadPeriodFromTime,
			this.cargo?.loadPeriodUntilDate
		)
	}

	get unloadingTerm() {
		return this.formatLoadingTerm(
			this.cargo?.unloadPeriodFromDate,
			this.cargo?.unloadPeriodFromTime,
			this.cargo?.unloadPeriodUntilDate
		)
	}

	addDefaultTransportInfos() {
		if (!this.transport) return
		this.comments += i18n.t('track-and-trace.transport_infos', {
			newline: '\n',
			clientOrderReference: this.cargo ? this.cargo.clientOrderReference : '',
			shipName: this.transport.getActiveVessel()?.name,
			skipperName: this.transport.getActiveVessel()?.skipperName,
			dimensions: this.transport.getActiveVessel()?.dimensions,
			phoneNumber: this.transport.getActiveVessel()?.phoneNumber,
			loadingPlace: this.transport.getCargo().startPlace,
			startTerminal: this.startTerminal?.name,
			unloadingPlace: this.transport.getCargo().destinationPlace,
			endTerminal: this.endTerminal?.name,
			productName: this.transport.getCargo().product,
			quantity: this.transport.getCargo().quantity,
			loadingTerm: this.loadingTerm,
			unloadingTerm: this.unloadingTerm
		}) as string
	}

	async send() {
		if (!(await this.$validator.validateAll())) return

		this.loading = true

		try {
			this.setLocalStorageAndUpdate()

			await this.createTrackTrace()

			await Notification.Success(i18n.t('track-and-trace.success'))

			this.emailAddresses = []
			this.comments = ''
		} finally {
			this.loading = false
			this.closeTrackTrace()
		}
	}

	setLocalStorageAndUpdate() {
		const storedEmails = JSON.parse(localStorage.getItem(this.localStorageKey) ?? '{}')

		for (const email in storedEmails) {
			if (this.emailAddresses.includes(email)) {
				storedEmails[email]++
			} else {
				storedEmails[email] = 0
			}
		}

		this.emailAddresses.forEach((email) => {
			if (!storedEmails[email]) {
				storedEmails[email] = 1
			}
		})

		window.localStorage.setItem(this.localStorageKey, JSON.stringify(storedEmails))
	}

	async createTrackTrace() {
		const trackAndTrace = new TrackTraceHash()
		trackAndTrace.emailAddresses = this.emailAddresses.join(',')
		trackAndTrace.setRelation('transport', this.transport)
		trackAndTrace.comment = DOMPurify.sanitize(this.comments)

		return (await trackAndTrace.save()).getModel() as TrackTraceHash
	}

	checkIfEmailAddressIsValid(emailAddresses: Array<string>) {
		this.errorMessage = ''

		this.emailAddresses = emailAddresses.filter((emailAddress) => {
			const isEmailValid = checkIfEmailAddressIsValid(emailAddress)

			if (!isEmailValid) {
				this.errorMessage = this.$t('track-and-trace.invalid_email', {
					email: emailAddress
				}).toString()
			}

			return isEmailValid
		})
	}

	closeTrackTrace() {
		this.$emit('dialog-closed')
	}
}
